import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AccessRequestDto, MemberDto, TeamDto } from '@rp/contracts';
import { api } from '../lib/api';
import { Badge, Button, Card, ScreenTitle, Section, Skeleton } from '../components/ui';
import { formatDate } from '../lib/format';
import { useSession } from '../hooks/session';
import { alert, confirm, haptic } from '../lib/telegram';

const ROLE_LABEL: Record<string, string> = {
  OWNER: 'Владелец',
  ADMIN: 'Администратор',
  OPERATOR: 'Оператор',
  VIEWER: 'Наблюдатель',
};

const ROLE_HINT: Record<string, string> = {
  OWNER: 'Полный доступ, включая передачу прав',
  ADMIN: 'Подключения, настройки и приём сотрудников',
  OPERATOR: 'Ответы на отзывы и публикация',
  VIEWER: 'Только просмотр отзывов и аналитики',
};

/** Роли, которые можно назначить сотруднику: владелец назначается отдельно. */
const ASSIGNABLE = ['ADMIN', 'OPERATOR', 'VIEWER'] as const;

/**
 * Сотрудники организации и заявки на доступ.
 *
 * Те же кнопки есть в чате бота: админ подтверждает заявку там, где ему удобнее.
 * Этот экран нужен, когда сообщение в чате пропустили или заявок накопилось много.
 */
export function TeamPage() {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const team = useQuery({ queryKey: ['team'], queryFn: api.team });

  const apply = (data: TeamDto) => {
    haptic.success();
    queryClient.setQueryData(['team'], data);
  };
  const fail = (error: Error) => {
    haptic.error();
    alert(error.message);
  };

  const decide = useMutation({
    mutationFn: ({ id, approve, role }: { id: string; approve: boolean; role?: string }) =>
      api.decideAccess(id, approve, role),
    onSuccess: apply,
    onError: fail,
  });

  const setRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) => api.setMemberRole(userId, role),
    onSuccess: apply,
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (userId: string) => api.removeMember(userId),
    onSuccess: apply,
    onError: fail,
  });

  if (team.isLoading) {
    return (
      <div className="mx-auto max-w-lg space-y-2 px-4 pt-3">
        <Skeleton className="h-28" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  if (!team.data) {
    return <p className="p-8 text-center text-[var(--color-hint)]">Не удалось загрузить список сотрудников</p>;
  }

  const { members, pendingRequests } = team.data;

  return (
    <div className="mx-auto max-w-lg">
      <ScreenTitle title="Сотрудники" />
      {can.manageTeam && pendingRequests.length > 0 && (
        <Section title={`Заявки на доступ · ${pendingRequests.length}`}>
          <div className="space-y-2 px-4">
            {pendingRequests.map((request) => (
              <RequestCard
                key={request.id}
                request={request}
                busy={decide.isPending && decide.variables?.id === request.id}
                onDecide={(approve, role) => decide.mutate({ id: request.id, approve, role })}
              />
            ))}
          </div>
        </Section>
      )}

      <Section title={`Сотрудники · ${members.length}`}>
        <Card className="mx-4 p-0">
          <ul className="divide-y divide-[var(--color-separator)]">
            {members.map((member) => (
              <MemberRow
                key={member.userId}
                member={member}
                editable={can.manageTeam}
                busy={setRole.isPending || remove.isPending}
                onRole={(role) => setRole.mutate({ userId: member.userId, role })}
                onRemove={() => {
                  void confirm(`Удалить ${member.name} из организации?`).then((confirmed) => {
                    if (confirmed) remove.mutate(member.userId);
                  });
                }}
              />
            ))}
          </ul>
        </Card>
      </Section>

      <Card className="mx-4 mb-6">
        <p className="t-footnote leading-relaxed text-[var(--color-hint)]">
          {can.manageTeam
            ? 'Чтобы добавить сотрудника, попросите его открыть бота и нажать «Запросить доступ» — заявка появится здесь и придёт вам в чат. Подключения к 2ГИС и Яндексу общие: повторно входить в кабинеты новому сотруднику не нужно.'
            : 'Состав команды и роли меняет администратор организации. Подключения к площадкам общие — вам не нужно входить в кабинеты 2ГИС и Яндекса.'}
        </p>
      </Card>
    </div>
  );
}

function RequestCard({
  request,
  busy,
  onDecide,
}: {
  request: AccessRequestDto;
  busy: boolean;
  onDecide: (approve: boolean, role?: string) => void;
}) {
  return (
    <Card className="mx-4">
      <p className="t-body font-medium">{request.user.name}</p>
      <p className="t-footnote text-[var(--color-hint)]">
        {request.user.username ? `@${request.user.username} · ` : ''}
        {formatDate(request.createdAt)}
      </p>

      <div className="mt-3 flex gap-2">
        <Button className="flex-1" loading={busy} onClick={() => onDecide(true, 'OPERATOR')}>
          Подтвердить
        </Button>
        <Button variant="danger" disabled={busy} onClick={() => onDecide(false)}>
          Отклонить
        </Button>
      </div>
      <p className="mt-2 t-caption text-[var(--color-hint)]">
        Будет добавлен как оператор — сможет отвечать на отзывы. Роль можно изменить после.
      </p>
    </Card>
  );
}

function MemberRow({
  member,
  editable,
  busy,
  onRole,
  onRemove,
}: {
  member: MemberDto;
  editable: boolean;
  busy: boolean;
  onRole: (role: string) => void;
  onRemove: () => void;
}) {
  const isOwner = member.role === 'OWNER';

  return (
    <li className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="t-callout font-medium">
            {member.name}
            {member.isSelf && <span className="ml-1.5 t-footnote text-[var(--color-hint)]">— это вы</span>}
          </p>
          <p className="t-footnote text-[var(--color-hint)]">
            {member.username ? `@${member.username} · ` : ''}с {formatDate(member.joinedAt)}
          </p>
        </div>
        <Badge color={isOwner ? 'var(--color-accent)' : undefined}>{ROLE_LABEL[member.role]}</Badge>
      </div>

      {editable && !isOwner && !member.isSelf && (
        <>
          <div className="mt-3 flex gap-1.5">
            {ASSIGNABLE.map((role) => (
              <button
                key={role}
                disabled={busy}
                onClick={() => {
                  haptic.select();
                  onRole(role);
                }}
                className="flex-1 rounded-lg py-1.5 t-caption font-medium disabled:opacity-40"
                style={
                  member.role === role
                    ? { background: 'var(--color-accent)', color: 'var(--color-on-accent)' }
                    : { background: 'var(--color-surface)', color: 'var(--color-hint)' }
                }
              >
                {ROLE_LABEL[role]}
              </button>
            ))}
          </div>
          <p className="mt-1.5 t-caption text-[var(--color-hint)]">{ROLE_HINT[member.role]}</p>
          <Button variant="danger" className="mt-1 -ml-4" disabled={busy} onClick={onRemove}>
            Удалить из организации
          </Button>
        </>
      )}
    </li>
  );
}
